import Common from '@/libs/common';
import type { OnlineDevice } from '@/@types/device';
import { StopType } from '@/@types/trip-updates';
import type { StopTimeData } from '@/@types/gtfs-ressource';
import type { TripDetailRow } from './types';

export type BusState = 'stopped' | 'transit';

export interface TripRowsSplit {
  /** Already-served stops, rendered greyed above the vehicle. */
  passed: TripDetailRow[];
  /** Stops the vehicle has yet to serve. */
  upcoming: TripDetailRow[];
  /** Last passed stop, carrying the "passé il y a…" label. */
  passedRow?: TripDetailRow;
  /** The vehicle's next stop, hosting the enriched block. */
  currentRow?: TripDetailRow;
}

/**
 * Splits the trip's rows around the vehicle position: the whole timeline stays
 * visible, served stops greyed out and the next stop promoted to current row.
 */
export function splitTripRows(
  rows: TripDetailRow[],
  currentStopSequence: number | null | undefined,
): TripRowsSplit {
  if (currentStopSequence == null) return { passed: [], upcoming: rows };

  // A sequence beyond the terminus (degraded realtime data) would grey out the
  // whole trip: no stop is marked as served instead.
  let passed = rows.filter(st => st.stopSequence < currentStopSequence);
  if (passed.length === rows.length) passed = [];

  const upcoming = passed.length > 0 ? rows.slice(passed.length) : rows;
  const first = upcoming[0];
  return {
    passed,
    upcoming,
    passedRow: passed[passed.length - 1],
    // A first row behind the vehicle (out-of-range sequence) is not "next"
    currentRow: first && first.stopSequence >= currentStopSequence ? first : undefined,
  };
}

/**
 * The row of the stop the passenger opened the trip from. On a trip serving it
 * twice (a loop), the passage still to come wins over the one already served.
 */
export function findOriginRow(
  split: TripRowsSplit,
  isOriginStop: (stopId: string) => boolean,
): TripDetailRow | undefined {
  return (
    split.upcoming.find(row => isOriginStop(row.stopId)) ??
    [...split.passed].reverse().find(row => isOriginStop(row.stopId))
  );
}

export interface TimelineRowState {
  variant: 'passed' | 'current' | 'regular';
  /** The stop the passenger opened the trip from: green block. */
  highlighted: boolean;
  /** Hosts the "more times" drawer. */
  enriched: boolean;
  label?: 'passedAgo' | 'plannedIn';
}

/**
 * How a row of the timeline reads. The enriched block goes to the passenger's own
 * stop when they came from one, to the vehicle's next stop otherwise, and never to
 * a canceled stop. With an origin stop, the vehicle's next stop stays a plain row.
 */
export function timelineRowState(
  row: TripDetailRow,
  passed: boolean,
  {
    originRow,
    currentRow,
    passedRow,
  }: { originRow?: TripDetailRow; currentRow?: TripDetailRow; passedRow?: TripDetailRow },
): TimelineRowState {
  const focusRow = originRow ?? currentRow;
  const hasEnrichedFocus = !!focusRow && focusRow.type !== StopType.CANCELED;
  const highlighted = row === originRow && hasEnrichedFocus;
  const enriched = row === focusRow && hasEnrichedFocus;

  if (passed) {
    return {
      variant: 'passed',
      highlighted,
      enriched,
      label: row === passedRow || highlighted ? 'passedAgo' : undefined,
    };
  }
  return {
    variant: !originRow && enriched ? 'current' : 'regular',
    highlighted,
    enriched,
    label: highlighted || (row === currentRow && row.type !== StopType.CANCELED) ? 'plannedIn' : undefined,
  };
}

/** One marker per vehicle, keyed by the row of the stop it heads to. */
export function buildDeviceMarkers(devices: OnlineDevice[], rows: TripDetailRow[]): Map<number, BusState> {
  const bySequence = new Map<number, BusState>();
  devices.forEach(device => {
    const seq = device.current_stop_sequence;
    if (seq == null) return;
    const state: BusState = device.current_status === 'STOPPED_AT' ? 'stopped' : 'transit';
    // Snap to the row of the next stop, like splitTripRows: sequences may skip
    // numbers, and a vehicle in such a gap heads to the first row beyond it.
    const row = rows.find(st => st.stopSequence >= seq);
    if (row) bySequence.set(row.stopSequence, state);
  });
  return bySequence;
}

export interface NextDeparture {
  tripId: string;
  label: string;
}

/**
 * Next departures of the same route and direction at a stop, excluding the
 * displayed trip. Sorted before deduplication so a trip serving the stop
 * twice keeps its next passage.
 */
export function buildNextDepartures(
  stopTimes: StopTimeData[],
  options: { routeId: string; tripHeadsign: string; excludedTripId: string; nowTs: number; max?: number },
): NextDeparture[] {
  const { routeId, tripHeadsign, excludedTripId, nowTs, max = 4 } = options;
  const seenTripIds = new Set<string>([excludedTripId]);
  return stopTimes
    .filter(
      st =>
        st.route_id === routeId &&
        st.trip_headsign === tripHeadsign &&
        !st.last_stop &&
        (st.estimated_departure_ts || st.departure_ts) > nowTs,
    )
    .sort(
      (a, b) => (a.estimated_departure_ts || a.departure_ts) - (b.estimated_departure_ts || b.departure_ts),
    )
    .filter(st => {
      if (seenTripIds.has(st.trip_id)) return false;
      seenTripIds.add(st.trip_id);
      return true;
    })
    .slice(0, max)
    .map(st => {
      const ts = st.estimated_departure_ts || st.departure_ts;
      const label = ts - nowTs < 3600 ? Common.formatSec(ts - nowTs) : Common.timestampFormatHHMM(ts);
      return { tripId: st.trip_id, label };
    });
}
